/**
 * Run prepared-drop creates for duplicate / copy-as-draft.
 * Uses the one-request bulk endpoint (grouped per campaign, chunked) when available,
 * else falls back to the legacy per-item create. Reports progress as rows land.
 *
 * One undoGroupId is minted per CALL and sent with every request it makes: a
 * copy spanning campaigns, or over 200 items, is several requests, and without
 * a shared id "undo" would take back only the last chunk.
 * For Media Devoted | Created By Ciro Autiero
 */

const MAX_BULK_ITEMS = 200;

export interface PreparedCreateItem {
  countryKey: string;
  wiserId: number;
  payload: Record<string, unknown>;
}

export interface PreparedCreateDeps {
  bulkCreatePrepared?: {
    mutateAsync: (p: {
      countryKey: string;
      wiserId: number;
      items: Record<string, unknown>[];
      undoGroupId?: string;
    }) => Promise<unknown>;
  };
  createPreparedFlex: {
    mutateAsync: (p: {
      countryKey: string;
      wiserId: number;
      payload: Record<string, unknown>;
      undoGroupId?: string;
    }) => Promise<unknown>;
  };
}

export async function runPreparedCreates(
  items: PreparedCreateItem[],
  deps: PreparedCreateDeps,
  onProgress: (done: number) => void,
): Promise<void> {
  const undoGroupId = crypto.randomUUID();
  if (deps.bulkCreatePrepared) {
    await runBulk(items, deps.bulkCreatePrepared, onProgress, undoGroupId);
    return;
  }
  let done = 0;
  for (const it of items) {
    await deps.createPreparedFlex.mutateAsync({ ...it, undoGroupId });
    onProgress((done += 1));
  }
}

async function runBulk(
  items: PreparedCreateItem[],
  bulk: NonNullable<PreparedCreateDeps['bulkCreatePrepared']>,
  onProgress: (done: number) => void,
  undoGroupId: string,
): Promise<void> {
  let done = 0;
  for (const group of groupByCampaign(items)) {
    for (let s = 0; s < group.items.length; s += MAX_BULK_ITEMS) {
      const chunk = group.items.slice(s, s + MAX_BULK_ITEMS);
      await bulk.mutateAsync({
        countryKey: group.countryKey,
        wiserId: group.wiserId,
        items: chunk.map((i) => i.payload),
        undoGroupId,
      });
      onProgress((done += chunk.length));
    }
  }
}

function groupByCampaign(
  items: PreparedCreateItem[],
): Array<{ countryKey: string; wiserId: number; items: PreparedCreateItem[] }> {
  const map = new Map<string, { countryKey: string; wiserId: number; items: PreparedCreateItem[] }>();
  for (const it of items) {
    const k = `${it.countryKey}::${it.wiserId}`;
    const g = map.get(k) ?? { countryKey: it.countryKey, wiserId: it.wiserId, items: [] };
    g.items.push(it);
    map.set(k, g);
  }
  return [...map.values()];
}
